/**
 * IMPORTANT: English comments only. DO NOT use Chinese characters in comments.
 * All Chinese comments will be removed during code reviews.
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { subscriptionService, deliveryService } from '../services';
import apiClient from '../services/api';
import { toast } from 'react-hot-toast';
import { SubscriptionState } from '../types';
import { EnhancedDeliverySchedule } from './useSubscriptionData';
import { useNavigate, NavigateFunction } from 'react-router-dom';
import { Address } from '../types/api';

// Define locally to avoid import errors
type DeliveryStatus = 'pending' | 'scheduled' | 'in_progress' | 'delivered' | 'skipped' | 'canceled' | 'projected';
type SubscriptionStatus = 'active' | 'paused' | 'cancelled' | 'pending_renewal' | 'past_due';

interface UseSubscriptionActionsProps {
  subscriptions: SubscriptionState[];
  setSubscriptions: React.Dispatch<React.SetStateAction<SubscriptionState[]>>;
  activeSubscriptionIndex: number;
  upcomingDeliveries: EnhancedDeliverySchedule[];
  setUpcomingDeliveries: React.Dispatch<React.SetStateAction<EnhancedDeliverySchedule[]>>;
  setProcessingDeliveryId: React.Dispatch<React.SetStateAction<string | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  fetchSubscriptionData: (subscriptionId?: string, forceRefresh?: boolean) => Promise<void>;
}

interface UseSubscriptionActionsResult {
  activeSubscription: SubscriptionState;
  showSkipModal: boolean;
  setShowSkipModal: React.Dispatch<React.SetStateAction<boolean>>;
  skipDate: string | null;
  setSkipDate: React.Dispatch<React.SetStateAction<string | null>>;
  handleSkipDelivery: () => Promise<void>;
  handleSkipDeliveryFromHistory: (deliveryId: string, date: string) => Promise<void>;
  handlePauseSubscription: () => Promise<void>;
  handleResumeSubscription: () => Promise<void>;
  handleCancelSubscription: (navigate: NavigateFunction) => Promise<void>;
  handleScheduleUpdate: (schedule: { dayOfWeek: number; timeSlot: string }) => Promise<void>;
  handleAddressUpdate: (address?: Address) => Promise<void>;
}

export const useSubscriptionActions = ({
  subscriptions,
  setSubscriptions,
  activeSubscriptionIndex,
  upcomingDeliveries,
  setUpcomingDeliveries,
  setProcessingDeliveryId,
  setLoading,
  fetchSubscriptionData
}: UseSubscriptionActionsProps): UseSubscriptionActionsResult => {
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [skipDate, setSkipDate] = useState<string | null>(null);
  
  const activeSubscription = subscriptions[activeSubscriptionIndex] || {};

  const handleSkipDelivery = async () => {
    if (!skipDate) {
      toast.error('No delivery date selected');
      return;
    }
    
    const subscriptionId = activeSubscription.id;
    if (!subscriptionId) {
      toast.error('No active subscription found');
      return;
    }
    
    setLoading(true);
    try {
      
      const deliveryToSkip = upcomingDeliveries.find(
        delivery => format(new Date(delivery.date), 'yyyy-MM-dd') === skipDate
      );
      
      let deliveryId;
      
      
      if (!deliveryToSkip) {
        try {
          const createdDelivery = await deliveryService.createDelivery(
            skipDate,
            activeSubscription.deliverySettings?.time_slots?.[0] || "9:00 AM - 12:00 PM",
            subscriptionId
          );
          
          if (createdDelivery) {
            
            deliveryId = (createdDelivery as any).id || `temp-${subscriptionId}-${skipDate}`;
            
            
            const newDelivery = {
              ...createdDelivery,
              id: deliveryId,  
              subscriptionId,
              timeSlot: createdDelivery.timeSlot || "9:00 AM - 12:00 PM"
            };
            
            const updatedDeliveries = [...upcomingDeliveries, newDelivery];
            updatedDeliveries.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            setUpcomingDeliveries(updatedDeliveries);
          }
        } catch (error) {
          console.error('Error creating delivery:', error);
          toast.error('Failed to create delivery');
          setLoading(false);
          return;
        }
      } else {
        deliveryId = deliveryToSkip.id || skipDate;
      }
      
      if (!deliveryId) {
        toast.error('Failed to identify delivery to skip');
        setLoading(false);
        return;
      }
      
      setProcessingDeliveryId(deliveryId);
      
      // Check if deliveryId is a date string (YYYY-MM-DD format)
      const isDateFormat = deliveryId && /^\d{4}-\d{2}-\d{2}$/.test(deliveryId);
      
      let updatedDelivery;
      if (isDateFormat) {
        // If deliveryId is actually a date, pass it as the date parameter and empty string as deliveryId
        updatedDelivery = await deliveryService.skipDelivery('', subscriptionId, deliveryId);
      } else {
        // Use regular deliveryId
        updatedDelivery = await deliveryService.skipDelivery(deliveryId, subscriptionId);
      }
      
      if (updatedDelivery) {
        
        
        const updatedDeliveries = upcomingDeliveries.map(delivery => {
          if ((delivery as EnhancedDeliverySchedule).id === deliveryId || 
              (delivery.date && format(new Date(delivery.date), 'yyyy-MM-dd') === skipDate)) {
            return { ...delivery, status: 'skipped' as DeliveryStatus };
          }
          return delivery;
        });
        
        setUpcomingDeliveries(updatedDeliveries);
        
        
        if (subscriptions.length > 0 && activeSubscriptionIndex >= 0) {
          const updatedSubscriptions = [...subscriptions];
          const subscription = {...updatedSubscriptions[activeSubscriptionIndex]};
          
          // Extend the subscription end date by one week
          if (subscription.endDate) {
            const endDate = new Date(subscription.endDate);
            endDate.setDate(endDate.getDate() + 7);
            subscription.endDate = endDate.toISOString();
          }
          
          // Find the next non-skipped delivery date that is in the future
          const now = new Date();
          const nextDelivery = updatedDeliveries.find(
            delivery => 
              new Date(delivery.date) > now && 
              delivery.status !== 'skipped'
          );
          
          // Update the next delivery date if we found a valid one
          if (nextDelivery) {
            console.log(`Setting next delivery date to ${nextDelivery.date}`);
            subscription.nextDeliveryDate = nextDelivery.date;
          } else {
            console.log('No upcoming non-skipped deliveries found');
          }
          
          // Update the subscription in state
          updatedSubscriptions[activeSubscriptionIndex] = subscription;
          setSubscriptions(updatedSubscriptions);
          
          // Force a data refresh to get additional projected deliveries if needed
          await fetchSubscriptionData(subscription.id, true);
        }
        
        toast.success('Delivery skipped successfully');
        setShowSkipModal(false);
      } else {
        toast.error('Failed to skip delivery');
      }
    } catch (error) {
      console.error('Error skipping delivery:', error);
      toast.error('Failed to skip delivery');
    } finally {
      setLoading(false);
      setProcessingDeliveryId(null);
    }
  };

  
  const handleSkipDeliveryFromHistory = async (deliveryId: string, date: string) => {
    console.log('Skip delivery called with:', { deliveryId, date });
    
    // Check if we have date (required for all deliveries)
    if (!date) {
      console.error('Invalid date received:', date);
      toast.error('Invalid delivery date');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to skip the delivery on ${format(new Date(date), 'MMMM d, yyyy')}?`)) {
      console.log('Skip delivery canceled by user');
      return;
    }
    
    // Check if subscription ID exists
    if (!activeSubscription.id) {
      console.error('No active subscription found');
      toast.error('No active subscription found');
      return;
    }
    
    // Use delivery ID as processing ID if available, otherwise use a generated ID based on date
    const processingId = deliveryId || `temp-${activeSubscription.id}-${date}`;
    setProcessingDeliveryId(processingId);
    setLoading(true);
    
    try {
      console.log(`Skipping delivery ${deliveryId || '(using date)'} for subscription ${activeSubscription.id}`);
      
      // Now we're sure activeSubscription.id is defined
      // Check if deliveryId is a date string (YYYY-MM-DD format)
      const isDateFormat = deliveryId && /^\d{4}-\d{2}-\d{2}$/.test(deliveryId);
      
      let updatedDelivery;
      if (isDateFormat) {
        // If deliveryId is actually a date, pass it as the date parameter and empty string as deliveryId
        updatedDelivery = await deliveryService.skipDelivery('', activeSubscription.id, deliveryId);
      } else {
        // Otherwise use deliveryId and date as before
        updatedDelivery = await deliveryService.skipDelivery(deliveryId, activeSubscription.id, date);
      }
      
      if (updatedDelivery) {
        console.log('Skip delivery successful:', updatedDelivery);
        
        const updatedUpcoming = upcomingDeliveries.map(delivery => {
          // Match by ID if available
          const matchesById = deliveryId && delivery.id === deliveryId;
          // Match by date for calculated deliveries without ID
          const matchesByDate = delivery.date && format(new Date(delivery.date), 'yyyy-MM-dd') === format(new Date(date), 'yyyy-MM-dd');
          
          if (matchesById || matchesByDate) {
            return { ...delivery, status: 'skipped' as DeliveryStatus };
          }
          return delivery;
        });
        
        setUpcomingDeliveries(updatedUpcoming);
        
        
        if (subscriptions.length > 0 && activeSubscriptionIndex >= 0) {
          const updatedSubscriptions = [...subscriptions];
          const subscription = {...updatedSubscriptions[activeSubscriptionIndex]};
          
          // Extend the subscription end date by one week
          if (subscription.endDate) {
            const endDate = new Date(subscription.endDate);
            endDate.setDate(endDate.getDate() + 7);
            subscription.endDate = endDate.toISOString();
          }
          
          // Find the next non-skipped delivery date that is in the future
          const now = new Date();
          const nextDelivery = updatedUpcoming.find(
            delivery => 
              new Date(delivery.date) > now && 
              delivery.status !== 'skipped'
          );
          
          // Update the next delivery date if we found a valid one
          if (nextDelivery) {
            console.log(`Setting next delivery date to ${nextDelivery.date}`);
            subscription.nextDeliveryDate = nextDelivery.date;
          } else {
            console.log('No upcoming non-skipped deliveries found');
          }
          
          // Update the subscription in state
          updatedSubscriptions[activeSubscriptionIndex] = subscription;
          setSubscriptions(updatedSubscriptions);
          
          // Force a data refresh to get additional projected deliveries if needed
          await fetchSubscriptionData(subscription.id, true);
        }
        
        toast.success('Delivery skipped successfully');
      } else {
        toast.error('Failed to skip delivery');
      }
    } catch (error) {
      console.error('Error skipping delivery:', error);
      toast.error('Failed to skip delivery');
    } finally {
      setLoading(false);
      setProcessingDeliveryId(null);
    }
  };

  // Handle pause subscription
  const handlePauseSubscription = async () => {
    if (!activeSubscription.id) {
      toast.error('No subscription ID found');
      return;
    }
    
    const subscriptionId = activeSubscription.id;
    console.log('BEFORE PAUSE - Active subscription:', {
      id: subscriptionId,
      status: activeSubscription.status,
      index: activeSubscriptionIndex
    });
    
    setLoading(true);
    
    // Immediately update UI to show change (optimistic update)
    const updatedSubscriptions = [...subscriptions];
    const updatedSubscriptionObj = {
      ...activeSubscription,
      status: 'paused' as SubscriptionStatus
    };
    updatedSubscriptions[activeSubscriptionIndex] = updatedSubscriptionObj;
    setSubscriptions(updatedSubscriptions);
    
    try {
      console.log('Pausing subscription:', subscriptionId);
      
      // Call API to pause subscription
      const updatedSubscription = await subscriptionService.updateStatus(
        subscriptionId, 
        'paused' as any
      );
      
      console.log('API response for pause:', updatedSubscription);
      
      // Update local state
      if (updatedSubscription) {
        console.log('Updating subscription state to paused:', updatedSubscriptionObj);
        
        // Force a refresh of subscription data immediately
        console.log('Forcing immediate data refresh after pause');
        
        try {
          console.log('⚠️ SENDING FORCED API REFRESH REQUEST ⚠️');
          
          // Use the fetchSubscriptionData function with forceRefresh=true instead
          await fetchSubscriptionData(subscriptionId, true);
          
          console.log('✅ SUBSCRIPTION DATA REFRESHED FROM SERVER');
        } catch (refreshError) {
          console.error('Error refreshing data after pause:', refreshError);
        }
        
        toast.success('Subscription paused successfully');
      } else {
        toast.error('Failed to pause subscription');
        
        // Revert optimistic update if failed
        const revertedSubscriptions = [...subscriptions];
        revertedSubscriptions[activeSubscriptionIndex] = {
          ...activeSubscription,
          status: 'active' as SubscriptionStatus
        };
        setSubscriptions(revertedSubscriptions);
      }
    } catch (error) {
      console.error('Error pausing subscription:', error);
      toast.error('Failed to pause subscription');
      
      // Revert optimistic update if error
      const revertedSubscriptions = [...subscriptions];
      revertedSubscriptions[activeSubscriptionIndex] = {
        ...activeSubscription,
        status: 'active' as SubscriptionStatus
      };
      setSubscriptions(revertedSubscriptions);
    } finally {
      setLoading(false);
    }
  };

  // Handle resume subscription
  const handleResumeSubscription = async () => {
    if (!activeSubscription.id) {
      toast.error('No subscription ID found');
      return;
    }
    
    const subscriptionId = activeSubscription.id;
    console.log('BEFORE RESUME - Active subscription:', {
      id: subscriptionId,
      status: activeSubscription.status,
      index: activeSubscriptionIndex
    });
    
    setLoading(true);
    
    // Immediately update UI to show change (optimistic update)
    const updatedSubscriptions = [...subscriptions];
    const updatedSubscriptionObj = {
      ...activeSubscription,
      status: 'active' as SubscriptionStatus
    };
    updatedSubscriptions[activeSubscriptionIndex] = updatedSubscriptionObj;
    setSubscriptions(updatedSubscriptions);
    
    try {
      console.log('Resuming subscription:', subscriptionId);
      
      // Call API to resume subscription
      const updatedSubscription = await subscriptionService.updateStatus(
        subscriptionId, 
        'active' as any
      );
      
      console.log('API response for resume:', updatedSubscription);
      
      // Update local state
      if (updatedSubscription) {
        console.log('Updating subscription state to active:', updatedSubscriptionObj);
        
        // Force a refresh of subscription data immediately
        console.log('Forcing immediate data refresh after resume');
        
        try {
          console.log('⚠️ SENDING FORCED API REFRESH REQUEST ⚠️');
          
          // Use the fetchSubscriptionData function with forceRefresh=true instead
          await fetchSubscriptionData(subscriptionId, true);
          
          console.log('✅ SUBSCRIPTION DATA REFRESHED FROM SERVER');
        } catch (refreshError) {
          console.error('Error refreshing data after resume:', refreshError);
        }
        
        toast.success('Subscription resumed successfully');
      } else {
        toast.error('Failed to resume subscription');
        
        // Revert optimistic update if failed
        const revertedSubscriptions = [...subscriptions];
        revertedSubscriptions[activeSubscriptionIndex] = {
          ...activeSubscription,
          status: 'paused' as SubscriptionStatus
        };
        setSubscriptions(revertedSubscriptions);
      }
    } catch (error) {
      console.error('Error resuming subscription:', error);
      toast.error('Failed to resume subscription');
      
      // Revert optimistic update if error
      const revertedSubscriptions = [...subscriptions];
      revertedSubscriptions[activeSubscriptionIndex] = {
        ...activeSubscription,
        status: 'paused' as SubscriptionStatus
      };
      setSubscriptions(revertedSubscriptions);
    } finally {
      setLoading(false);
    }
  };

  
  const handleCancelSubscription = async (navigate: NavigateFunction) => {
    if (!activeSubscription.id) {
      toast.error('No subscription ID found');
      return;
    }
    
    const subscriptionId = activeSubscription.id;
    
    if (!window.confirm('Are you sure you want to cancel your subscription? Your future deliveries will be cancelled and a refund will be processed for undelivered orders.')) {
      return;
    }
    
    setLoading(true);
    try {
      // Use the remaining_deliveries from the active subscription
      const undeliveredCount = activeSubscription.remainingDeliveries || 0;
      
      // Update subscription status to cancelled
      const updatedSubscription = await subscriptionService.updateStatus(
        subscriptionId, 
        'cancelled'
      );
      
      // Create refund request
      try {
        await apiClient.post('/refunds/create', {
          subscriptionId,
          reason: 'subscription_cancellation',
          undeliveredCount,
          estimatedAmount: undeliveredCount * (activeSubscription.price || 0)
        });
        
        if (undeliveredCount > 0) {
          toast.success(`Refund request for ${undeliveredCount} undelivered orders has been created.`);
        }
      } catch (refundError) {
        console.error('Error creating refund request:', refundError);
        toast.error('Could not process refund request');
      }
      
      
      if (updatedSubscription) {
        const updatedSubscriptions = [...subscriptions];
        updatedSubscriptions[activeSubscriptionIndex] = {
          ...activeSubscription,
          ...updatedSubscription,
          status: 'cancelled' as SubscriptionStatus
        } as SubscriptionState;
        setSubscriptions(updatedSubscriptions);
        
        toast.success('Subscription cancelled successfully');
        
        
        setTimeout(() => {
          navigate('/flowers');
        }, 1500);
      } else {
        toast.error('Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  
  const handleScheduleUpdate = async (newSchedule: { dayOfWeek: number; timeSlot: string }) => {
    if (!activeSubscription.id) {
      toast.error('No subscription ID found');
      return;
    }
    
    setLoading(true);
    try {
      
      const updatedSubscriptions = [...subscriptions];
      updatedSubscriptions[activeSubscriptionIndex] = {
        ...updatedSubscriptions[activeSubscriptionIndex],
        deliverySettings: {
          ...updatedSubscriptions[activeSubscriptionIndex].deliverySettings,
          day_of_week: newSchedule.dayOfWeek,
          time_slots: [newSchedule.timeSlot]
        }
      };
      setSubscriptions(updatedSubscriptions);
      toast.success('Schedule updated successfully');
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error('Failed to update schedule');
    } finally {
      setLoading(false);
    }
  };

  
  const handleAddressUpdate = async (newAddress?: Address) => {
    if (!activeSubscription.id) {
      console.error('[useSubscriptionActions] No subscription ID found for address update');
      return;
    }
    
    if (!newAddress) {
      console.error('[useSubscriptionActions] No address provided for update');
      return;
    }
    
    // Make sure the address is valid before trying to update
    if (!newAddress.street || !newAddress.city) {
      console.error('[useSubscriptionActions] Invalid address provided:', newAddress);
      return;
    }
    
    const subscriptionId = activeSubscription.id;
    
    setLoading(true);
    try {
      const updatedSubscription = await subscriptionService.updateSubscriptionAddress(
        subscriptionId,
        newAddress
      );
      
      if (updatedSubscription) {
        const updatedSubscriptions = [...subscriptions];
        updatedSubscriptions[activeSubscriptionIndex] = {
          ...updatedSubscriptions[activeSubscriptionIndex],
          address: newAddress
        };
        
        setSubscriptions(updatedSubscriptions);
        toast.success('Address updated successfully');
      } else {
        toast.error('Failed to update address');
      }
    } catch (error) {
      console.error('Error updating address:', error);
      toast.error('Failed to update address');
    } finally {
      setLoading(false);
    }
  };

  return {
    activeSubscription,
    showSkipModal,
    setShowSkipModal,
    skipDate,
    setSkipDate,
    handleSkipDelivery,
    handleSkipDeliveryFromHistory,
    handlePauseSubscription,
    handleResumeSubscription,
    handleCancelSubscription,
    handleScheduleUpdate,
    handleAddressUpdate
  };
}; 